import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Progress } from "@/components/ui/progress";

type GlobalLoaderContextType = {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  sincronizandoText: string;
  loadingStep: string;
  setLoadingStep: (step: string) => void;
  loadingProgress: number;
  setLoadingProgress: (progress: number) => void;
};

const GlobalLoaderContext = createContext<GlobalLoaderContextType | undefined>(
  undefined,
);

export function GlobalLoaderProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [sincronizandoText, setSincronizandoText] = useState(
    t("loader.syncing"),
  );
  const [loadingStep, setLoadingStep] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);

  useEffect(() => {
    if (isLoading) {
      setSincronizandoText(t("loader.syncing"));

      const timer10s = setTimeout(() => {
        setSincronizandoText(t("loader.syncingSlow"));
      }, 10000);

      const timer60s = setTimeout(() => {
        setSincronizandoText(t("loader.syncingStill"));
      }, 60000);

      const timer15min = setTimeout(
        () => {
          setSincronizandoText(t("loader.syncingLong"));
        },
        15 * 60 * 1000,
      );

      const timer30min = setTimeout(
        () => {
          setSincronizandoText(t("loader.syncingContactSupport"));
        },
        30 * 60 * 1000,
      );

      return () => {
        clearTimeout(timer10s);
        clearTimeout(timer60s);
        clearTimeout(timer15min);
        clearTimeout(timer30min);
      };
    } else {
      setLoadingStep("");
      setLoadingProgress(0);
    }
  }, [isLoading]);

  return (
    <GlobalLoaderContext.Provider
      value={{
        isLoading,
        setIsLoading,
        sincronizandoText,
        loadingStep,
        setLoadingStep,
        loadingProgress,
        setLoadingProgress,
      }}
    >
      {children}
      <GlobalLoader />
    </GlobalLoaderContext.Provider>
  );
}

export function useGlobalLoader() {
  const context = useContext(GlobalLoaderContext);
  if (!context) {
    throw new Error(
      "useGlobalLoader deve ser usado dentro de GlobalLoaderProvider",
    );
  }
  return context;
}

function GlobalLoader() {
  const context = useContext(GlobalLoaderContext);
  if (!context) return null;

  const { isLoading, sincronizandoText, loadingStep, loadingProgress } =
    context;

  if (!isLoading) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-black/70"
      style={{ zIndex: 9999 }}
    >
      <div className="animate-spin">
        <Loader size={50} color="white" />
      </div>

      <div className="flex flex-col items-center gap-4 w-80">
        <div className="text-white text-lg font-medium">
          {sincronizandoText}
        </div>

        {loadingStep && (
          <>
            <Progress
              value={loadingProgress}
              className="w-full h-3 bg-white/20"
            />
            <div className="flex justify-between w-full">
              <span className="text-white/80 text-sm">{loadingStep}</span>
              <span className="text-white/80 text-sm">{loadingProgress}%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
